import React, { useState, useEffect } from 'react';
import { X, Trash2, Check, Weight, TrendingUp, TrendingDown, Minus, Plus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getPetWeightHistory, addPetWeightRecord, deletePetWeightRecord } from '../services/api';
import { toast } from 'sonner';

export default function PetWeightModal({ petId, petName, onClose }) {
  const [records, setRecords] = useState([]);
  const [weightKg, setWeightKg] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { loadRecords(); }, [petId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecords = async () => {
    try { const res = await getPetWeightHistory(petId); setRecords(res.data); } catch (e) { console.error(e); }
  };

  const handleSubmit = async () => {
    const kg = parseFloat(weightKg);
    if (!kg || kg <= 0) { toast.error('Geçerli bir ağırlık girin'); return; }
    setSaving(true);
    try {
      await addPetWeightRecord(petId, { weight_grams: Math.round(kg * 1000), note: note.trim() });
      setWeightKg(''); setNote('');
      loadRecords();
      toast.success('Kilo kaydedildi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (recordId) => {
    try { await deletePetWeightRecord(petId, recordId); setDeletingId(null); loadRecords(); toast.success('Kayıt silindi'); }
    catch (e) { toast.error('Silinemedi'); }
  };

  const formatWeight = (grams) => {
    if (grams >= 1000) return `${(grams / 1000).toFixed(1)} kg`;
    return `${grams} g`;
  };

  const getWeightChange = (index) => {
    if (index >= records.length - 1) return null;
    const current = records[index].weight_grams;
    const prev = records[index + 1].weight_grams;
    const diff = current - prev;
    if (diff === 0) return null;
    return diff;
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="weight-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Weight size={16} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{petName} - Kilo Takibi</h2>
              <p className="text-xs text-[#8E8E93]">
                {records.length > 0 ? `Son: ${formatWeight(records[0].weight_grams)}` : 'Kayıt yok'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="close-weight-modal">
            <X size={20} className="text-[#8E8E93]" />
          </button>
        </div>

        {/* Add Weight */}
        <div className="px-5 pt-4 pb-3 shrink-0 space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input type="number" step="0.1" min="0" value={weightKg} onChange={e => setWeightKg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
                placeholder="Ağırlık" className="h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm pr-10" data-testid="weight-input" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93] font-medium">kg</span>
            </div>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Not (opsiyonel)"
              className="flex-1 h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="weight-note-input" />
            <Button onClick={handleSubmit} disabled={saving || !weightKg}
              className="h-10 w-10 p-0 bg-amber-500 hover:bg-amber-600 rounded-xl disabled:opacity-40" data-testid="weight-submit">
              <Plus size={16} />
            </Button>
          </div>
        </div>

        {/* Weight History */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {records.length > 0 ? (
            <div className="space-y-2">
              {records.map((r, i) => {
                const change = getWeightChange(i);
                return (
                  <div key={r.id} className="flex items-center gap-3 p-3 bg-[#F2F2F7] rounded-xl group" data-testid={`weight-${r.id}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      change > 0 ? 'bg-green-100' : change < 0 ? 'bg-red-100' : 'bg-gray-200'
                    }`}>
                      {change > 0 ? <TrendingUp size={16} className="text-green-600" /> :
                       change < 0 ? <TrendingDown size={16} className="text-red-600" /> :
                       <Minus size={16} className="text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-base font-bold text-gray-900">{formatWeight(r.weight_grams)}</span>
                        {change !== null && (
                          <span className={`text-[11px] font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {change > 0 ? '+' : ''}{formatWeight(Math.abs(change))}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-[10px] text-[#8E8E93]">
                          {new Date(r.date + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {r.created_by_name && <span> · <span className="text-amber-600">{r.created_by_name}</span></span>}
                        </p>
                      </div>
                      {r.note && <p className="text-xs text-gray-600 mt-0.5">{r.note}</p>}
                    </div>
                    {deletingId === r.id ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded-full bg-red-100 hover:bg-red-200"><Check size={14} className="text-[#FF3B30]" /></button>
                        <button onClick={() => setDeletingId(null)} className="p-1.5 rounded-full hover:bg-white"><X size={14} className="text-[#8E8E93]" /></button>
                      </div>
                    ) : (
                      <button onClick={() => setDeletingId(r.id)} className="p-1.5 rounded-full hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Trash2 size={14} className="text-[#FF3B30]" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Weight size={36} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-sm text-[#8E8E93]">Henüz kilo kaydı yok</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
